import { format } from 'date-fns';
import { createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';

export const ENGAGEMENT_PERSONAS_LIMIT = 5000;

export const ACCOUNT_TIER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'];

export function getNextScheduledRaw(p) {
  if (!p || typeof p !== 'object') return null;
  const v =
    p.next_scheduled ??
    p.next_scheduled_at ??
    p.next_send_at ??
    p.scheduled_send_at ??
    p.next_touch_scheduled_at ??
    null;
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s === 'null') return null;
  return s;
}

function startOfTodayMs() {
  const x = new Date();
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function parseScheduledMs(iso) {
  if (iso === null || iso === undefined) return null;
  const s = String(iso).trim();
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
}

function mergeNextScheduledForPair(rawA, rawB) {
  const items = [];
  for (const raw of [rawA, rawB]) {
    const ms = parseScheduledMs(raw);
    if (ms != null) items.push({ raw, ms });
  }
  if (items.length === 0) return null;
  const t0 = startOfTodayMs();
  const upcoming = items.filter((o) => o.ms >= t0);
  if (upcoming.length > 0) {
    return upcoming.reduce((best, cur) => (cur.ms < best.ms ? cur : best)).raw;
  }
  return items.reduce((best, cur) => (cur.ms > best.ms ? cur : best)).raw;
}

export function engagementPersonaDedupeKey(p, fallbackIndex) {
  const pid = sanitizeId(p?.person_id ?? p?.persona_id);
  if (pid) return `pid::${pid}`;
  const email = String(p?.person_email || p?.email || '').trim().toLowerCase();
  if (email) return `em::${email}`;
  const aid = sanitizeId(p?.account_id);
  const aname = String(p?.account_name || '').trim().toLowerCase();
  if (aid && aname) return `acct::${aid}::${aname}`;
  if (aid) return `acct::${aid}::${fallbackIndex}`;
  return `row::${fallbackIndex}`;
}

function mergeEngagementPersonaPair(a, b) {
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  const latest = (x, y) => {
    if (!y) return x ?? y;
    if (!x) return y;
    const tx = new Date(x).getTime();
    const ty = new Date(y).getTime();
    if (!Number.isFinite(tx)) return y;
    if (!Number.isFinite(ty)) return x;
    return tx >= ty ? x : y;
  };
  const touchesResolved = (r) => resolvePersonaTouches(r);
  const li = (r) =>
    r?.linkedin_connected === true || r?.linkedin_connected === 1 || r?.linkedin_connected === 'true';
  const out = { ...a };
  out.human_opens = Math.max(n(a.human_opens), n(b.human_opens));
  out.human_clicks = Math.max(n(a.human_clicks), n(b.human_clicks));
  out.replies = Math.max(n(a.replies), n(b.replies));
  out.last_human_open_at = latest(a.last_human_open_at, b.last_human_open_at);
  out.last_human_click_at = latest(a.last_human_click_at, b.last_human_click_at);
  out.last_touch_at = latest(a.last_touch_at, b.last_touch_at);
  out.last_message_sent_at = latest(a.last_message_sent_at, b.last_message_sent_at);
  out.last_sent_at = latest(a.last_sent_at, b.last_sent_at);
  out.last_interaction_at = latest(a.last_interaction_at, b.last_interaction_at);
  out.next_scheduled = mergeNextScheduledForPair(getNextScheduledRaw(a), getNextScheduledRaw(b));
  out.linkedin_connected = li(a) || li(b);
  out.touches_count = Math.max(touchesResolved(a), touchesResolved(b));
  out.emails_sent = Math.max(n(a.emails_sent), n(b.emails_sent));
  out.linkedin_sent = Math.max(
    n(a.linkedin_sent ?? a.linkedin_messages_sent),
    n(b.linkedin_sent ?? b.linkedin_messages_sent),
  );
  out.linkedin_messages_sent = Math.max(n(a.linkedin_messages_sent), n(b.linkedin_messages_sent));
  out.call_sent = Math.max(n(a.call_sent ?? a.calls_sent), n(b.call_sent ?? b.calls_sent));
  out.product_affinity = pickFirstNonEmpty(a.product_affinity, b.product_affinity, out.product_affinity);

  const engagementScore = (r) => n(r.human_clicks) * 10000 + n(r.human_opens);
  const winner = engagementScore(b) > engagementScore(a) ? b : a;
  const loser = winner === b ? a : b;
  out.account_id = sanitizeId(winner.account_id) || sanitizeId(loser.account_id) || sanitizeId(out.account_id);
  out.account_name =
    String(winner.account_name || '').trim() ||
    String(loser.account_name || '').trim() ||
    String(out.account_name || '').trim();
  out.archetype_name = pickFirstNonEmpty(
    a.archetype_name,
    a.archetype,
    b.archetype_name,
    b.archetype,
    out.archetype_name,
    out.archetype,
  );
  out.department = pickFirstNonEmpty(a.department, b.department, out.department);

  return out;
}

export function dedupeEngagementPersonas(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const map = new Map();
  rows.forEach((p, i) => {
    const k = engagementPersonaDedupeKey(p, i);
    if (map.has(k)) map.set(k, mergeEngagementPersonaPair(map.get(k), p));
    else map.set(k, p);
  });
  return Array.from(map.values());
}

export function formatNextScheduled(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'MMM d, h:mm a');
}

export function formatRel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

export function isSameLocalDay(iso, now = new Date()) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function startOfLocalDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

export function calendarDaysFromLastTouchToToday(iso) {
  if (!iso) return null;
  const touch = new Date(iso);
  if (Number.isNaN(touch.getTime())) return null;
  const touchDay = startOfLocalDay(touch);
  const today = startOfLocalDay(new Date());
  return Math.round((today - touchDay) / 86400000);
}

export function matchesLastTouchFilter(iso, filterKey) {
  if (!filterKey || filterKey === 'all') return true;
  if (!iso) return false;
  const d = calendarDaysFromLastTouchToToday(iso);
  if (d === null || d < 0) return false;
  if (filterKey === 'today') return d === 0;
  if (filterKey === 'yesterday') return d === 1;
  if (filterKey === 'days_2') return d === 2;
  if (filterKey === 'days_3') return d === 3;
  if (filterKey === 'days_4') return d === 4;
  if (filterKey === 'days_5') return d === 5;
  if (filterKey === 'days_6') return d === 6;
  if (filterKey === 'days_7_plus') return d >= 7;
  return true;
}

export function formatLastTouchCalendarLabel(iso) {
  if (!iso) return '—';
  const d = calendarDaysFromLastTouchToToday(iso);
  if (d === null) return '—';
  if (d < 0) return 'Future';
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d >= 2 && d <= 6) return `${d} days ago`;
  return '7+ days ago';
}

export function formatLastTouchTooltip(iso) {
  if (!iso) return 'No last touch timestamp';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return format(d, 'MMM d, yyyy h:mm a');
}

export function getPersonaTableDepartment(p) {
  if (!p || typeof p !== 'object') return '';
  return String(p.department ?? '').trim();
}

function pickFirstNonEmpty(...values) {
  for (const v of values) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

export function getPersonaArchetypeName(p) {
  if (!p || typeof p !== 'object') return '';
  return pickFirstNonEmpty(p.archetype_name, p.archetype, p.persona_archetype);
}

export function getPersonProfileUrl(p) {
  const personId = sanitizeId(p?.person_id || p?.id || p?.persona_id);
  if (!personId) return null;
  const accountId = sanitizeId(p?.account_id);
  const path = accountId
    ? `PersonProfile?id=${encodeURIComponent(personId)}&accountId=${encodeURIComponent(accountId)}`
    : `PersonProfile?id=${encodeURIComponent(personId)}`;
  return createPageUrl(path);
}

export function getAccountProfileUrl(p, accountIdByNameMap) {
  let accountId = sanitizeId(p?.account_id);
  if (!accountId && accountIdByNameMap) {
    const name = String(p?.account_name || '').trim().toLowerCase();
    if (name) accountId = sanitizeId(accountIdByNameMap.get(name));
  }
  if (!accountId) return null;
  return createPageUrl(`AccountFinal?id=${encodeURIComponent(accountId)}`);
}

export function getRowAccountFitScore(p) {
  const candidates = [
    p?.account_fit_score,
    p?.account_fit,
    p?.account_score,
    p?.fit_score_account,
    p?.account?.account_fit_score,
    p?.account?.account_fit,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function formatAccountFitIntegerDisplay(score) {
  if (score == null || score === '') return '—';
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  const v = n > 0 && n <= 1 ? Math.round(n * 100) : Math.round(n);
  return String(v);
}

export function normalizeAccountTier(tier) {
  if (tier == null || tier === '') return null;
  const raw = String(tier).trim();
  if (!raw) return null;
  const tierMatch = raw.match(/Tier\s*(\d)/i);
  if (tierMatch) return `Tier ${tierMatch[1]}`;
  const tMatch = raw.match(/^T(\d)$/i);
  if (tMatch) return `Tier ${tMatch[1]}`;
  if (/^\d$/.test(raw)) return `Tier ${raw}`;
  return raw;
}

export function deriveAccountTierFromFitScore(score, decisionWindowBand) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  const fit = n > 0 && n <= 1 ? n * 100 : n;
  const isAplus = fit >= 90 && decisionWindowBand === '<12m';
  if (isAplus || fit >= 90) return 'Tier 1';
  if (fit >= 75) return 'Tier 2';
  if (fit >= 60) return 'Tier 3';
  return 'Tier 4';
}

export function resolveAccountTierFromRecord(acc) {
  const explicit = normalizeAccountTier(acc?.account_tier ?? acc?.tier);
  if (explicit) return explicit;
  return deriveAccountTierFromFitScore(
    acc?.account_fit_score ?? acc?.account_fit ?? acc?.fit_score,
    acc?.decision_window_band,
  );
}

export function collectHiddenPersonIds(rows) {
  const set = new Set();
  for (const row of rows || []) {
    for (const key of ['person_id', 'id', 'persona_id']) {
      const id = sanitizeId(row?.[key]);
      if (id) set.add(id);
    }
  }
  return set;
}

export function isEngagementPersonaVisible(p, { hiddenAccountIds, hiddenAccountNames, hiddenPersonaIds }) {
  if (p?.is_visible === false) return false;
  const personId = sanitizeId(p?.person_id ?? p?.persona_id);
  if (personId && hiddenPersonaIds.has(personId)) return false;
  const accountId = String(p?.account_id || '').trim();
  if (accountId && hiddenAccountIds.has(accountId)) return false;
  const accountName = String(p?.account_name || '').trim().toLowerCase();
  if (accountName && hiddenAccountNames.has(accountName)) return false;
  return true;
}

export function appendEngagementVisibilityParams(params) {
  params.append('only_visible_accounts', 'true');
  params.append('only_visible_personas', 'true');
}

export function resolvePersonaTouches(p) {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const explicit =
    p?.touches_count ?? p?.total_touches ?? p?.total_interactions ?? p?.interactions ?? null;
  if (explicit !== null && explicit !== undefined && explicit !== '') return toNum(explicit);
  return (
    toNum(p?.emails_sent) +
    toNum(p?.linkedin_sent ?? p?.linkedin_messages_sent) +
    toNum(p?.call_sent ?? p?.calls_sent)
  );
}

export function getLastActivityAt(p) {
  const openTs = p?.last_human_open_at ? new Date(p.last_human_open_at).getTime() : 0;
  const clickTs = p?.last_human_click_at ? new Date(p.last_human_click_at).getTime() : 0;
  if (!openTs && !clickTs) return null;
  return openTs >= clickTs ? p?.last_human_open_at : p?.last_human_click_at;
}

export function getLastTouchAt(p) {
  return (
    p?.last_touch_at ||
    p?.last_message_sent_at ||
    p?.last_sent_at ||
    p?.last_interaction_at ||
    getLastActivityAt(p)
  );
}

export function sortEngagementPersonas(rows, sortConfig, helpers) {
  const { key, direction } = sortConfig || { key: 'human_clicks', direction: 'desc' };
  const sorted = [...rows];
  sorted.sort((a, b) => {
    let av;
    let bv;
    if (key === 'last_human_open_at' || key === 'last_human_click_at') {
      av = a?.[key] ? new Date(a[key]).getTime() : 0;
      bv = b?.[key] ? new Date(b[key]).getTime() : 0;
    } else if (key === 'last_activity_at') {
      const aOpen = a?.last_human_open_at ? new Date(a.last_human_open_at).getTime() : 0;
      const aClick = a?.last_human_click_at ? new Date(a.last_human_click_at).getTime() : 0;
      const bOpen = b?.last_human_open_at ? new Date(b.last_human_open_at).getTime() : 0;
      const bClick = b?.last_human_click_at ? new Date(b.last_human_click_at).getTime() : 0;
      av = Math.max(aOpen, aClick);
      bv = Math.max(bOpen, bClick);
    } else if (key === 'last_touch_at') {
      const aTouch = getLastTouchAt(a);
      const bTouch = getLastTouchAt(b);
      av = aTouch ? new Date(aTouch).getTime() : 0;
      bv = bTouch ? new Date(bTouch).getTime() : 0;
    } else if (key === 'next_scheduled') {
      const parse = (iso) => {
        if (!iso) return null;
        const t = new Date(iso).getTime();
        return Number.isNaN(t) ? null : t;
      };
      const aTime = parse(getNextScheduledRaw(a));
      const bTime = parse(getNextScheduledRaw(b));
      av = aTime === null ? (direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER) : aTime;
      bv = bTime === null ? (direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER) : bTime;
    } else if (key === 'linkedin_connected') {
      const isY = (p) =>
        p?.linkedin_connected === true ||
        p?.linkedin_connected === 1 ||
        p?.linkedin_connected === 'true';
      av = isY(a) ? (direction === 'asc' ? 0 : 1) : direction === 'asc' ? 1 : 0;
      bv = isY(b) ? (direction === 'asc' ? 0 : 1) : direction === 'asc' ? 1 : 0;
    } else if (key === 'person_name') {
      av = String(a?.person_name || a?.person_email || '').toLowerCase();
      bv = String(b?.person_name || b?.person_email || '').toLowerCase();
    } else if (key === 'account_name') {
      av = String(a?.account_name || '').toLowerCase();
      bv = String(b?.account_name || '').toLowerCase();
    } else if (key === 'archetype_name') {
      av = getPersonaArchetypeName(a).toLowerCase();
      bv = getPersonaArchetypeName(b).toLowerCase();
    } else if (key === 'account_fit_score') {
      av = helpers.getAccountFitScoreForRow(a) ?? -1;
      bv = helpers.getAccountFitScoreForRow(b) ?? -1;
    } else if (key === 'touches_count') {
      av = resolvePersonaTouches(a);
      bv = resolvePersonaTouches(b);
    } else {
      av = Number(a?.[key] || 0);
      bv = Number(b?.[key] || 0);
    }
    if (av < bv) return direction === 'asc' ? -1 : 1;
    if (av > bv) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
}

export function buildVisibleEngagementPersonas(data, visibilityScope, { sortEngaged = true } = {}) {
  const rows = Array.isArray(data?.personas) ? data.personas : [];
  const visibleRows = rows.filter((p) => isEngagementPersonaVisible(p, visibilityScope));
  const deduped = dedupeEngagementPersonas(visibleRows);
  if (!sortEngaged) return deduped;
  return deduped.sort((a, b) => {
    const clickDiff = (b?.human_clicks || 0) - (a?.human_clicks || 0);
    if (clickDiff !== 0) return clickDiff;
    const openDiff = (b?.human_opens || 0) - (a?.human_opens || 0);
    if (openDiff !== 0) return openDiff;
    return (b?.replies || 0) - (a?.replies || 0);
  });
}
